import React, { createContext, useContext, useState, useEffect } from 'react';
import creditService from '../services/creditService';

const CreditConfigContext = createContext();

const normalizeResolutionKey = (resolution) => {
  const raw = (resolution ?? '').toString().trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  const match = lower.match(/(\d+)\s*k/);
  if (match && match[1]) return `${match[1]}k`;
  return lower;
};

const normalizeModelSuffix = (model) => {
  const raw = (model ?? '').toString().trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower.includes('seedream')) return 'Seedream';
  if (lower.includes('gemini') || lower.includes('nano')) return 'Gemini';
  if (lower === 'g') return 'Gemini';
  if (lower === 's') return 'Seedream';
  return raw;
};

export const useCreditConfig = () => {
  const context = useContext(CreditConfigContext);
  if (!context) {
    throw new Error('useCreditConfig must be used within a CreditConfigProvider');
  }
  return context;
};

export const CreditConfigProvider = ({ children }) => {
  const [creditConfig, setCreditConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadCreditConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        const config = await creditService.getCreditSystemConfig();
        
        if (isMounted) {
          setCreditConfig(config);
        }
      } catch (error) {
        console.error('加载积分配置失败:', error);
        if (isMounted) {
          setError(error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCreditConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  const getToolDefaultCost = () => {
    if (!creditConfig) return 4;
    if (typeof creditConfig.toolDefaultCost === 'number') return creditConfig.toolDefaultCost;
    if (typeof creditConfig.defaultToolCost === 'number') return creditConfig.defaultToolCost;
    if (creditConfig.imageGeneration && typeof creditConfig.imageGeneration.defaultCost === 'number') {
      return creditConfig.imageGeneration.defaultCost;
    }
    return 4;
  };

  const getToolCost = (toolId) => {
    if (!toolId) return getToolDefaultCost();
    const byTool = creditConfig?.toolCosts && creditConfig.toolCosts[toolId];
    if (byTool && typeof byTool.defaultCost === 'number') return byTool.defaultCost;
    return getToolDefaultCost();
  };

  const getUpscalerCredits = (scale = 2) => {
    if (!creditConfig?.imageGeneration) {
      return 4; // 默认值
    }
    const { costByResolution } = creditConfig.imageGeneration;
    const key = `costByResolution_${scale}x`;
    if (costByResolution && costByResolution[key]) {
      return costByResolution[key];
    }
    return getToolDefaultCost() * (Number(scale) || 1);
  };

  const getImageGenerationCredits = ({ model, resolution, toolId } = {}) => {
    const defaultCost = getToolCost(toolId);
    const costByResolution = creditConfig?.imageGeneration?.costByResolution;
    if (!costByResolution || typeof costByResolution !== 'object') return defaultCost;

    const resKey = normalizeResolutionKey(resolution);
    const modelSuffix = normalizeModelSuffix(model);

    if (resKey && modelSuffix) {
      const key = `costByResolution_${resKey}_${modelSuffix}`;
      const val = costByResolution[key];
      if (typeof val === 'number') return val;
    }

    if (resKey) {
      const key = `costByResolution_${resKey}`;
      const val = costByResolution[key];
      if (typeof val === 'number') return val;
    }

    const fallback = creditConfig?.imageGeneration?.defaultCost;
    if (typeof fallback === 'number') return fallback;
    return defaultCost;
  };

  const normalizeVideoResolution = (value) => (value ?? '').toString().trim();

  const normalizeVideoMode = (value) => (value ?? '').toString().trim();

  const parseDurationSeconds = (value) => {
    const raw = (value ?? '').toString().trim();
    if (!raw) return 0;
    const match = raw.match(/(\d+(\.\d+)?)/);
    if (!match) return 0;
    return Number(match[1]) || 0;
  };

  const getVideoGenerationCost = ({
    model,
    videoMode,
    videoResolution,
    videoDuration
  } = {}) => {
    if (!creditConfig?.videoGeneration) return null;
    const { costByResolution } = creditConfig.videoGeneration;
    if (!costByResolution || typeof costByResolution !== 'object') return null;
    const modelRaw = (model ?? '').toString().trim();
    const resolution = normalizeVideoResolution(videoResolution);
    const mode = normalizeVideoMode(videoMode);
    const duration = parseDurationSeconds(videoDuration);
    if (!modelRaw || !resolution || !mode || !duration) return null;

    const baseKey = `video_${modelRaw}_${resolution}`;
    const modeKey = `video_${modelRaw}_${mode}`;
    const base = Number(costByResolution[baseKey]);
    const multiplier = Number(costByResolution[modeKey]);
    if (!Number.isFinite(base) || !Number.isFinite(multiplier)) return null;
    const total = base * duration * multiplier;
    return Math.round(total);
  };

  const getLyricsGenerationCost = () => {
    const costs = creditConfig?.audioGeneration;
    if (!costs || typeof costs !== 'object') return 1;
    const value = Number(costs.audio_lyrics_default);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const getAudioGenerationCost = ({ audioModel, duration } = {}) => {
    const costs = creditConfig?.audioGeneration;
    if (!costs || typeof costs !== 'object') return null;
    const model = (audioModel || 'lyria-3-pro-preview').toString().trim();
    const seconds = parseDurationSeconds(duration) || (model.includes('pro') ? 60 : 30);
    const exact = Number(costs[`audio_${model}_${seconds}s`]);
    if (Number.isFinite(exact) && exact > 0) return exact;
    const fallback = Number(costs[model.includes('pro') ? 'audio_pro_default' : 'audio_clip_default']);
    return Number.isFinite(fallback) && fallback > 0 ? fallback : null;
  };

  const value = {
    creditConfig,
    loading,
    error,
    getToolDefaultCost,
    getToolCost,
    getUpscalerCredits,
    getImageGenerationCredits,
    getVideoGenerationCost,
    getLyricsGenerationCost,
    getAudioGenerationCost
  };

  return (
    <CreditConfigContext.Provider value={value}>
      {children}
    </CreditConfigContext.Provider>
  );
};
